import { unzipSync } from 'fflate';

export type RomVersion = 'firered-1.0' | 'firered-1.1' | 'dynasty';

interface KnownRom {
  version: RomVersion;
  label: string;
  /** Multiplayer depende dos endereços de memória desta versão. */
  multiplayer: boolean;
}

// SHA-1 conforme pret/pokefirered (README).
const KNOWN_ROMS: Record<string, KnownRom> = {
  '41cb23d8dccc8ebd7c649cd8fbb58eeace6e2fdc': {
    version: 'firered-1.0',
    label: 'Pokémon FireRed (EUA) 1.0',
    multiplayer: true,
  },
  dd5945db9b930750cb39d00c84da8571feebf417: {
    version: 'firered-1.1',
    label: 'Pokémon FireRed (EUA) 1.1',
    multiplayer: false,
  },
};

export interface RomInfo extends KnownRom {
  sha1: string;
}

export class RomError extends Error {}

export async function sha1Hex(data: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-1', data as Uint8Array<ArrayBuffer>);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Título e código do cabeçalho do cartucho (offset 0xA0), útil em mensagens de erro. */
export function romHeaderTitle(rom: Uint8Array): string {
  const bytes = rom.subarray(0xa0, 0xb0);
  return String.fromCharCode(...bytes).replace(/\0/g, ' ').trim();
}

export async function identifyRom(rom: Uint8Array): Promise<RomInfo> {
  const sha1 = await sha1Hex(rom);
  const known = KNOWN_ROMS[sha1];
  if (!known) {
    throw new RomError(
      `ROM não reconhecida (${romHeaderTitle(rom) || 'cabeçalho vazio'}, SHA-1 ${sha1}). ` +
        'Use o Pokémon FireRed original em inglês (EUA), sem patches ou traduções.',
    );
  }
  return { ...known, sha1 };
}

/** Lê o arquivo escolhido pelo jogador. Aceita .gba ou .zip contendo um .gba. */
export async function readRomFile(file: File): Promise<Uint8Array> {
  const data = new Uint8Array(await file.arrayBuffer());
  const isZip = data[0] === 0x50 && data[1] === 0x4b; // "PK"
  if (!isZip) return data;

  const entries = unzipSync(data, { filter: (f) => f.name.toLowerCase().endsWith('.gba') });
  const first = Object.values(entries)[0];
  if (!first) throw new RomError('O .zip não contém nenhum arquivo .gba.');
  return first;
}
